import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { CheckCircleIcon, LockSimpleIcon, ShieldWarningIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";

const fh = (iso) => (iso ? new Date(iso).toLocaleString("es-HN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const val = (v) => (v === true ? "Sí" : v === false ? "No" : v === null || v === undefined || v === "" ? "(vacío)" : String(v));

/** «Última actualización: usuario, fecha y hora» con acceso al historial completo. */
export function UltimaActualizacion({ u, onVer }) {
  if (!u) return null;
  return (
    <div className="text-xs text-text-secondary flex flex-wrap items-center gap-x-2 gap-y-1 mt-3" data-testid="cliente-ultima-actualizacion">
      <ClockCounterClockwiseIcon size={14} aria-hidden="true" />
      <span>Última actualización: <b className="text-text-primary">{u.usuario_nombre || u.usuario}</b> · {fh(u.fecha)}{u.accion ? ` · ${u.accion}` : ""}</span>
      <button type="button" onClick={onVer} className="min-h-[32px] underline text-agro-green" data-testid="cliente-ver-historial">Ver historial completo</button>
    </div>
  );
}

/** Historial de cambios del cliente (generado por el servidor, no editable) y permisos por acción. */
export default function CambiosTab({ cid }) {
  const [data, setData] = useState(null);
  const [acciones, setAcciones] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/clients/${cid}/cambios`).then(({ data: d }) => setData(d)).catch((e) => setError(apiErr(e)));
    api.get("/clients-acciones").then(({ data: d }) => setAcciones(d)).catch(() => setAcciones([]));
  }, [cid]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="cliente-cambios">
      <section className="xl:col-span-2 bg-card border border-border/60 rounded-xl p-4">
        <h3 className="text-sm font-semibold mb-1">Historial de cambios</h3>
        <p className="text-[11px] text-text-muted mb-3">Lo genera el sistema en cada acción: usuario, fecha y hora, acción y campos con su valor anterior y nuevo. No se puede editar.</p>
        {error ? <div className="text-sm text-red-300">{error}</div> : !data ? <div className="text-sm text-text-muted">Cargando…</div>
          : data.items.length === 0 ? <div className="text-sm text-text-secondary" data-testid="cambios-vacio">Aún no hay cambios registrados.</div> : (
            <ol className="space-y-3">
              {data.items.map((c) => (
                <li key={c.id} className="border-l-2 border-border pl-3" data-testid={`cambio-${c.id}`}>
                  <div className="text-sm"><b>{c.accion}</b></div>
                  <div className="text-[11px] text-text-muted">{c.usuario_nombre || c.usuario} · {c.usuario} · {fh(c.fecha)}</div>
                  {c.detalle && <div className="text-xs text-text-secondary mt-0.5">{c.detalle}</div>}
                  {(c.cambios || []).length > 0 && (
                    <div className="mt-1.5 overflow-x-auto">
                      <table className="text-xs min-w-[360px]">
                        <thead className="text-text-muted text-left"><tr><th className="pr-4 font-medium">Campo</th><th className="pr-4 font-medium">Antes</th><th className="font-medium">Después</th></tr></thead>
                        <tbody>
                          {c.cambios.map((x) => (
                            <tr key={x.campo}><td className="pr-4 py-0.5 text-text-secondary">{x.etiqueta}</td>
                              <td className="pr-4 py-0.5 line-through decoration-red-400/60 text-text-muted">{val(x.antes)}</td>
                              <td className="py-0.5 text-text-primary">{val(x.despues)}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
      </section>
      <section className="bg-card border border-border/60 rounded-xl p-4" data-testid="cliente-permisos">
        <h3 className="text-sm font-semibold mb-1">Tus permisos sobre clientes</h3>
        <p className="text-[11px] text-text-muted mb-3">Permiso que exige cada acción. Las sensibles requieren una facultad específica.</p>
        <ul className="space-y-2">
          {acciones.map((a) => (
            <li key={a.clave} className="text-xs flex gap-2" data-testid={`accion-${a.clave}`}>
              {a.permitido ? <CheckCircleIcon size={16} className="text-agro-green shrink-0" aria-label="Permitido" />
                : <LockSimpleIcon size={16} className="text-text-muted shrink-0" aria-label="No permitido" />}
              <div>
                <div className={a.permitido ? "text-text-primary" : "text-text-secondary"}>
                  {a.accion}{a.sensible && <span className="ml-1 inline-flex items-center gap-0.5 text-agro-yellow"><ShieldWarningIcon size={11} /> sensible</span>}
                </div>
                <div className="text-text-muted">{a.permitido ? "Permitido" : "No permitido"} · Requiere: {a.permiso}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
